import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Link, useHistory } from "react-router-dom";
import { fetchApi } from '../services/api';
import { useEffect } from 'react';
import { paths } from '../routes';
import { SvgIcon } from "@progress/kendo-react-common";
import { searchIcon } from '@progress/kendo-svg-icons';
import useLocalStorage from '../utils/useLocalStorage';
import { trapFocus } from '../utils/trapFocus';
import { formatHotkey, getCommandActions, isDialogOpen, isTypingTarget } from '../utils/useCommandActions';
import { getNavigationShortcuts } from './NavigationShortcuts';

type GlobalSearchResult = {
  Name: string;
  Link?: string;
  Hotkey?: string;
  Action?: () => void;
}

function getMenuSearch(): GlobalSearchResult[] {
  return [
    { Link: paths.Home, Name: 'Home' },
    { Link: paths.AccountNotifications, Name: 'Notifications' },
    /*{ Link: paths.LoadBoard, Name: 'Open Shipments' },
    { Link: paths.LoadBoardBidHistory, Name: 'Bid History' },*/
    { Link: "/Track/Index", Name: 'Track' },
    { Link: paths.ShiftTurnover, Name: 'Shift Turnover' },
    { Link: paths.ServiceExceptions, Name: 'Service Exceptions' },
    { Link: paths.TrackPositions, Name: 'Positions' },
    { Link: paths.DeadHeadCreate, Name: 'Deadhead Trip' },
    { Link: paths.DeadHeadCreateHourlyDriver, Name: 'Hourly Driver Non-Billable Trip' },
    { Link: paths.PayTripCreate, Name: 'Additional Pay Trip' },
    { Link: "/Quote/Index", Name: 'New Quote' },
    { Link: paths.QuoteHistory, Name: 'Quote History' },
    { Link: paths.QuoteFinder, Name: 'Quote Finder' },
    { Link: paths.BlindBidQuote, Name: 'New Blind Bid' },
    { Link: paths.CarrierLoadBoard, Name: 'Carrier Load Board' },
    { Link: "/Assets/Drivers", Name: 'Drivers' },
    { Link: "/Assets/Vehicles", Name: 'Vehicles' },
    { Link: paths.AssetTrailers, Name: 'Trailers' },
    { Link: paths.AssetOwners, Name: 'Owners' },
    { Link: paths.AssetsCarriers, Name: 'Carriers' },
    { Link: "/Assets/DriverScoreDisputes", Name: 'Driver Score Disputes' },
    { Link: "/Assets/CarrierScoreDisputes", Name: 'Carrier Score Disputes' },
    { Link: paths.CarrierContactsGrid, Name: 'Carrier Contacts' },
    { Link: paths.CarrierGroups, Name: 'Carrier Groups' },
    { Link: paths.AssetInspections, Name: 'Inspections' },
    { Link: paths.FreightClaims, Name: 'Cargo Claims' },
    { Link: "/Driver/Chat", Name: 'Driver Chat' },
    { Link: paths.DriverAccidents, Name: 'Accidents / Incidents' },
    { Link: paths.LoadMap, Name: 'Load Map' },
    { Link: paths.VirtualTerminals, Name: 'Virtual Terminals' },
    { Link: paths.ComplianceDates, Name: 'Compliance Dates' },
    { Link: paths.DriverCardLogs, Name: 'Card Logs' },
    { Link: paths.UploadBestpassTollReport, Name: 'Upload Bestpass Toll Report' },
    { Link: paths.DriverMetrics, Name: 'Driver Metrics' },
    { Link: paths.WorkloadHistory, Name: 'Workload History' },
    { Link: paths.FleetMessages, Name: 'Fleet Messages' },
    { Link: paths.EquipmentCounts, Name: 'Equipment Counts' },
    { Link: paths.Customers, Name: 'Customers' },
    { Link: paths.CustomerContactsGrid, Name: 'Customers Contacts' },
    { Link: paths.Salespeople, Name: 'Salespeople' },
    { Link: paths.CustomsBrokers, Name: 'Customs Brokers' },
    { Link: paths.EmailAliases, Name: 'Email Aliases' },
    { Link: paths.RateMasterViewer, Name: 'Tariff Rate Structures' },
    { Link: paths.PayRateLinehaul, Name: 'Pay Rate Linehaul' },
    { Link: paths.PayRateAccessorial, Name: 'Pay Rate Accessorial' },
    { Link: paths.CustomersMerge, Name: 'Merge Customers' },
    { Link: paths.CustomersMassUpdateSalespeople, Name: 'Mass Update Salespeople' },
    { Link: paths.CustomersBackhaul, Name: 'Backhaul Assistant' },
    { Link: paths.InvoiceListing, Name: 'Invoice Listing' },
    { Link: paths.PostInvoiceBatch, Name: 'Post Invoice Batch' },
    { Link: paths.DownloadInvoiceBatch, Name: 'Download Invoice Batch' },
    { Link: paths.InvoiceGateway, Name: 'Invoice Gateway' },
    { Link: paths.UploadVehicleFuelDeductions, Name: 'Upload Vehicle Fuel Deductions' },
    { Link: paths.UploadDriverChargebacks, Name: 'Upload Driver/Vehicle Chargebacks' },
    { Link: paths.UploadEmailOwnerFuelReport, Name: 'Upload/Email Owner Fuel Report' },
    { Link: paths.UploadAmortizedVehicleChargebacks, Name: 'Upload Amortized Vehicle Chargebacks' },
    { Link: "/PurchaseOrder/Index", Name: 'Purchase Orders' },
    { Link: paths.ShopInventory, Name: 'Shop Inventory' },
    { Link: paths.ShopWorkOrders, Name: 'Shop Work Orders' },

    { Link: paths.CompanySettings, Name: 'Company Settings' },
    { Link: paths.QuoteAutomationRules, Name: 'Quote Rules' },
    { Link: paths.AccountSettings, Name: 'User Settings' },
    { Link: paths.AgentNotes, Name: 'Agent Notes' },
    { Link: paths.KnowledgeBase, Name: 'Knowledge Base' },
  ];
}

const defaultSuggestions = ['Track', 'New Quote', 'Quote History', 'Drivers', 'Customers', 'Invoice Listing'];

function getContextActions(): GlobalSearchResult[] {
  return getCommandActions().map(action => ({ Name: action.name, Hotkey: action.hotkey, Action: action.action }));
}

type Props = {
  buttonless?: boolean;
}

const GlobalSearch = (props: Props) => {

  const history = useHistory();

  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [results, setResults] = React.useState<GlobalSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [recentSelections, setRecentSelections] = useLocalStorage<GlobalSearchResult[]>("GlobalSearch_Recent", []);

  const resetSearch = () => {
    setOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
    setResults([]);
  }

  const getSuggestions = () => [
    ...getContextActions(),
    ...(recentSelections.length > 0 ? recentSelections : getMenuSearch().filter(item => defaultSuggestions.includes(item.Name)))
  ];

  const saveRecentSelection = (result: GlobalSearchResult) => {
    setRecentSelections(recent => [result, ...recent.filter(x => x.Link !== result.Link)].slice(0, 6));
  };

  const openSearch = () => {
    setOpen(true);
    setSelectedIndex(0);
    setResults(getSuggestions());
  };

  const handleShortcuts = (event: KeyboardEvent) => {

    if (open) {
      // Previous
      if (event.key === 'ArrowUp' && selectedIndex > 0) {
        setSelectedIndex(prevState => prevState - 1);
        event.preventDefault();
        return;
      }

      // Next
      if (event.key === 'ArrowDown' && (selectedIndex + 1) < results.length) {
        setSelectedIndex(prevState => prevState + 1);
        event.preventDefault();
        return;
      }

      // Exit
      if (event.key === 'Escape') {
        resetSearch();
        return;
      }

      // Select Item
      if (event.key === 'Enter' && results.length) {
        const result = results[selectedIndex];
        if (result.Action) {
          resetSearch();
          result.Action();
          return;
        }
        saveRecentSelection(result);
        if (result.Link.startsWith("https://")) {
          window.location.href = result.Link;
        } else {
          history.push(result.Link);
        }
        resetSearch();
      }
      return;
    }

    // Ignore if in other inputs
    if (isTypingTarget(event)) {
      return;
    }

    // Activate Global Search
    if (event.key === '/' && !isDialogOpen()) {
      openSearch();
      event.preventDefault();
    }
  };

  // Re-registered every render so the handler always sees current state
  useEffect(() => {
    window.addEventListener("keydown", handleShortcuts);
    return () => window.removeEventListener("keydown", handleShortcuts);
  });

  const search = (searchTerm: string) => {

    if (searchTerm.length < 1) {
      setResults(getSuggestions());
      setSelectedIndex(0);
      return;
    }

    // Local Results
    const localResults = [
      ...getContextActions().filter(item => item.Name.toLowerCase().includes(searchTerm.toLowerCase())),
      ...getMenuSearch().filter(item => item.Name.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 10)
    ];
    setResults(localResults);

    if (searchTerm.length < 3) {
      return;
    }

    searchOnline(searchTerm, localResults);
  }

  const searchOnline = useDebouncedCallback((searchTerm: string, localResults: GlobalSearchResult[]) => {
    const data = {
      SearchTerm: searchTerm
    }

    fetchApi('/api/User/GlobalSearch', data, 'POST')
      .then((response: GlobalSearchResult[]) => {
        setSelectedIndex(0);
        setResults([...localResults, ...response]);
      });
  }, 250);

  const overlay = open && <div
      className="global-search-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) resetSearch(); }}
    >
      <div
        className="card global-search-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        onKeyDown={trapFocus}
      >
        <input
          autoFocus
          className="form-control global-search-input"
          type="search"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); search(e.target.value.trim()); }}
          placeholder="Search pages, orders, customers..."
          aria-label="Search" />
        {results.length > 0 && <ul className="list-group list-group-flush global-search-results">
          {results.map((result, index) => {
            if (result.Action) {
              return <button
                key={index}
                type="button"
                onClick={() => { resetSearch(); result.Action(); }}
                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedIndex === index ? "active" : ""}`}
              >
                {result.Name}
                {result.Hotkey && <kbd className="d-none d-md-inline">{formatHotkey(result.Hotkey)}</kbd>}
              </button>;
            }
            const navHotkey = getNavigationShortcuts().find(s => s.link === result.Link)?.hotkey;
            return result.Link.startsWith("https://")
              ? <a
                key={index}
                href={result.Link}
                target="_blank" rel="noopener noreferrer"
                onClick={() => { saveRecentSelection(result); resetSearch(); }}
                className={`list-group-item ${selectedIndex === index ? "active" : ""}`}>
                {result.Name}
              </a>
              : <Link
                key={index}
                to={result.Link}
                onClick={() => { saveRecentSelection(result); resetSearch(); }}
                className={`list-group-item d-flex justify-content-between align-items-center ${selectedIndex === index ? "active" : ""}`}
              >
                {result.Name}
                {navHotkey && <kbd className="d-none d-md-inline">{formatHotkey(navHotkey)}</kbd>}
              </Link>
          })}
        </ul>}
        <div className="global-search-hints text-muted d-none d-md-block">
          <kbd>↑</kbd> <kbd>↓</kbd> navigate &nbsp;·&nbsp; <kbd>↵</kbd> open &nbsp;·&nbsp; <kbd>esc</kbd> close &nbsp;·&nbsp; <kbd>?</kbd> shortcuts
        </div>
      </div>
    </div>;

  if (props.buttonless) {
    return <>{overlay}</>;
  }

  return <li className="nav-item">
    <button
      type="button"
      className="navbar-pill"
      onClick={openSearch}
      aria-label="Search"
    >
      <SvgIcon icon={searchIcon} />
      <span className="d-none d-lg-inline">Search</span>
      <kbd className="d-none d-lg-inline">/</kbd>
    </button>
    {overlay}
  </li>;
}

export default GlobalSearch;
